/**
 * Whole-deck HTML cache for `html` slides, shared by the canvas casting view and the filmstrip
 * thumbnail. Every html slide's stored markup is fetched for the whole presentation in ONE call and
 * memoised module-side, so both the main render and every thumbnail read the same warm cache instead
 * of each fetching per slide.
 *
 * Every fragment is sanitised (see ./sanitize) as it enters the cache, so BOTH render sinks
 * — the casting view and the thumbnail — inject already-clean HTML into their shadow roots, and the
 * sanitising work happens once per slide per session rather than on every navigation.
 */

import { sanitizeSlideHtml } from './sanitize'
import { splitDeckHtml } from './split'

const htmlCache = new Map() // slideId -> sanitised html
const deckPrefetch = new Map() // presentationId -> in-flight Promise (dedup concurrent callers)

// A url-backed html slide stores only a reference (markupKey) to its HTML, hosted cross-origin in R2;
// the ~MB markup never sits in the slide row. Turn the fetched document into a shadow-ready fragment
// the same way an inline slide is built (splitDeckHtml adds the shadow fit override). If the hosted
// bytes are ALREADY a shadow fragment (they carry the fit-override marker), use them verbatim.
function hostedToShadowFragment(raw) {
  if (!raw) return ''
  if (raw.includes('data-aha-html-fit')) return raw
  const frag = splitDeckHtml(raw)[0]
  return frag ? frag.html : ''
}

// Fetch a url-backed slide's HTML from R2 and return clean, shadow-ready markup. The URL is public and
// CORS-open, so a plain fetch works on unauthenticated surfaces (share/embed, PDF capture) — do NOT
// attach app session credentials. R2 is not a trust boundary, so the bytes are sanitised before use.
// Any failure resolves to '' so one bad slide never rejects a whole-deck prefetch.
async function fetchHostedFragment(url) {
  try {
    const res = await fetch(url)
    if (!res.ok) return ''
    const raw = await res.text()
    return sanitizeSlideHtml(hostedToShadowFragment(raw))
  } catch (e) {
    return ''
  }
}

// Resolve one slide's `html` attribute to clean, render-ready markup: a url-backed slide (markupKey)
// is fetched from R2 and normalised; a legacy inline slide's markup is sanitised as before.
function resolveSlideHtml(attrHtml) {
  if (!attrHtml) return Promise.resolve('')
  if (attrHtml.markupKey) return fetchHostedFragment(attrHtml.markupKey)
  return Promise.resolve(sanitizeSlideHtml(attrHtml.markup || ''))
}

// Warm every html slide's markup for a presentation in one bulk fetch (idempotent per presentation).
export function prefetchDeck(store, presentationId) {
  if (!presentationId) return Promise.resolve()
  if (!deckPrefetch.has(presentationId)) {
    // Promise.resolve() so a store that doesn't register this root action (the slide-read-only /
    // embed build only has getSlideAttributesWithCacheAction) returns `undefined` → resolves to []
    // instead of throwing on `.then`. getSlideHtml then falls back to the per-slide action.
    const p = Promise.resolve(store.dispatch('getSlideAttributesByPresentationId', {
      presentationId, attributeKeys: 'html',
    })).then((rows) => Promise.all((rows || []).map(async (r) => {
      // Each slide resolves independently (resolveSlideHtml never rejects), so one slide whose R2
      // fetch fails leaves the others cached rather than failing the whole warm.
      if (!r || !r.slideId) return
      const clean = await resolveSlideHtml(r.attributes)
      if (clean) htmlCache.set(r.slideId, clean)
    }))).catch(() => { deckPrefetch.delete(presentationId) }) // let a failed prefetch retry
    deckPrefetch.set(presentationId, p)
  }
  return deckPrefetch.get(presentationId)
}

// Read one slide's HTML: cache-first (instant), else the whole-deck prefetch, else a single-slide
// fetch (covers a slide the bulk call didn't include, e.g. just inserted, and the read-only / embed
// store that has no bulk action). Returns '' if none.
export async function getSlideHtml(store, presentationId, slideId) {
  if (!slideId) return ''
  if (htmlCache.has(slideId)) return htmlCache.get(slideId)
  await prefetchDeck(store, presentationId)
  if (htmlCache.has(slideId)) return htmlCache.get(slideId)
  const attrs = await store.dispatch('getSlideAttributesWithCacheAction', slideId)
  const clean = await resolveSlideHtml(attrs && attrs.html)
  if (clean) htmlCache.set(slideId, clean)
  return clean
}

// Synchronous cache peek (no fetch) — for a render that wants the instant path only.
export function peekSlideHtml(slideId) {
  return htmlCache.get(slideId) || ''
}

// Drop one slide's cached markup so a live / re-import edit is re-fetched instead of served stale for
// the rest of the session. Wired to the same `slideAttributes/updateAttributes` signal the sibling
// attribute cache purges on (see htmlSlideCachePurgePlugin), so an html-slide edit arriving over live
// sync (MQTT kvSync) — or a local write — drops its entry; the next read then re-fetches via the
// single-slide path, whose attribute cache the sibling plugin purges on the same mutation. No-op when
// the slide isn't cached.
export function purgeSlideHtml(slideId) {
  if (slideId == null) return
  htmlCache.delete(slideId)
}
